import React, { useEffect, useState } from 'react';

import { RiPencilFill } from 'react-icons/ri';

import { Card, FormControl, TextField, Tooltip } from '@mui/material';

import SkeletonComponent from '../components/SkeletonComponent';
import { SavedSubmission } from '../types/formTypes';
import { useSubmitForm } from '../useSubmitForm';
import Button from 'components/Button';
import * as I from 'components/DynamicFormGroup/types/dynamicFormGroupTypes';
import Text from 'components/Text';
import { useEditStatus } from 'global/EditStatusContext';
import { useUser } from 'global/UserContext';
import { useDebounce } from 'hooks/useDebounce';

interface TextFormFieldProps {
  field: (I.TextField | I.TextAreaField) & { submission?: SavedSubmission };
}

const TextFormField: React.FC<TextFormFieldProps> = ({ field }) => {
  const { user } = useUser();
  const { setEditStatus } = useEditStatus();
  const [text, setText] = useState<string>('');
  const [isTextareaReadOnly, setIsTextareaReadOnly] = useState<boolean>(true);
  const debouncedText = useDebounce(text, 1000);

  const [prevLoading, setPrevLoading] = useState<boolean>(true);
  const [prevError, setPrevError] = useState<string | null>(null);

  const { submitForm } = useSubmitForm();
  const textLimit = field.field_type === 'textarea' ? 800 : 100;
  const user_id = user?.id;

  const debouncedEditStatus = useDebounce(text, 500);
  useEffect(() => {
    if (!isTextareaReadOnly && debouncedEditStatus.length <= textLimit) {
      setEditStatus(true, debouncedEditStatus);
    }
  }, [debouncedEditStatus]);

  useEffect(() => {
    // Initialize text from submission data instead of API call
    if (field.submission && field.submission.values && field.submission.values.length > 0) {
      setText(field.submission.values[0].value || '');
    }
    setPrevLoading(false);
  }, [field.submission]);

  useEffect(() => {
    const submitData = async () => {
      try {
        await submitForm(field.id, { values: [{ value: debouncedText }] });
        setEditStatus(false, '');
      } catch (error) {
        console.error('Error submitting data:', error);
      }
    };

    if (!isTextareaReadOnly && debouncedText.length <= textLimit) {
      submitData();
    } else if (debouncedText.length > textLimit) {
      console.error('Text exceeds the allowed limit');
    }
  }, [debouncedText]);

  const handleEditClick = () => {
    setIsTextareaReadOnly(false);
    setEditStatus(true, debouncedText);
  };

  const handleTextChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setText(e.target.value);
  };

  const handleBlur = () => {
    setIsTextareaReadOnly(true);
  };

  if (prevLoading) {
    return <SkeletonComponent />;
  }

  if (prevError) {
    return <div>Error loading data</div>;
  }

  return (
    <Card key={field.id}>
      <Text size="medium" bold>
        {field.label}
      </Text>
      <FormControl fullWidth>
        <Tooltip
          title={
            isTextareaReadOnly ? (
              <Text size="medium">É necessário liberar o modo de edição</Text>
            ) : (
              ''
            )
          }
          arrow
        >
          <TextField
            required={field.is_required}
            placeholder={field.extras?.placeholders?.[0] || ''}
            value={text}
            onChange={handleTextChange}
            onBlur={handleBlur}
            fullWidth
            variant="outlined"
            error={text.length > textLimit}
            multiline={field.field_type === 'textarea'}
            rows={7}
            style={{
              marginTop: '12px',
              cursor: isTextareaReadOnly ? 'not-allowed' : 'text',
            }}
            InputProps={{
              readOnly: isTextareaReadOnly,
            }}
            className="textfield"
          />
        </Tooltip>
        <div
          style={{
            width: '100%',
            display: 'flex',
            placeContent: 'space-between',
            placeItems: 'center',
          }}
        >
          <Text size="small" color={text.length > textLimit ? 'red' : 'textPrimary'}>
            {text.length}/{textLimit} caracteres
          </Text>
          {isTextareaReadOnly && (
            <Button
              icon={<RiPencilFill />}
              type="link"
              size="small"
              onClick={handleEditClick}
              className="edit-button"
              style={{ marginTop: '0.5rem' }}
            >
              Editar
            </Button>
          )}
        </div>
      </FormControl>
    </Card>
  );
};

export default TextFormField;
